/**
 * home/data/homeSources.ts
 * Fuentes de datos oficiales — cascada L1 completa (14 niveles R2 + fallback)
 *
 * AUDITORÍA 2026-03-06 (C.03 — fuentes R2 actualizadas):
 * - 14 niveles R2 precargados en Cloudflare (respuesta instantánea)
 * - CDAU: 97 % (761/785), 2,69M portales
 * - AGOL: operativo (L1.08), OBPH (L1.11), GC Facilities (L1.14)
 * - 4 fuentes fallback online (CartoCiudad, CDAU online, IDEE, Nominatim)
 */
import {
  IconDatabase, IconMap, IconMapPin, IconChart, IconPlus,
  IconGlobe, IconSearch, IconShieldCheck,
} from '../icons';
import type { SourceData } from './types';

/** Títulos de presentación por tier */
export const TIER_LABELS: Record<SourceData['tier'], string> = {
  masivas: 'Fuentes masivas (R2 — instantáneo)',
  sectoriales: 'Fuentes sectoriales (R2)',
  patrimoniales: 'Patrimonio cultural (R2)',
  especializadas: 'Fuentes especializadas (R2)',
  fallback: 'Fallback — APIs en línea',
};

/** Orden de presentación de los tiers */
export const TIER_ORDER: SourceData['tier'][] = [
  'masivas', 'sectoriales', 'patrimoniales', 'especializadas', 'fallback',
];

export const SOURCES: SourceData[] = [
  // ─── Masivas ───
  { name: 'CDAU R2', desc: 'Callejero Digital de Andalucía — 2,69M portales', badge: '97 % (761/785)', icon: IconMapPin, tier: 'masivas' },
  { name: 'Catastro R2', desc: 'Dirección General del Catastro — 2,4M edificaciones precargadas', badge: '100% municipios', icon: IconDatabase, tier: 'masivas',
    modePill: 'Híbrido',
    note: 'Ver también: Catastro API → consulta activa por referencia catastral en el nivel de respaldo' },
  { name: 'NGA R2', desc: 'Nomenclátor Geográfico de Andalucía — 1,8M topónimos', badge: '100% municipios', icon: IconMap, tier: 'masivas' },

  // ─── Sectoriales ───
  { name: 'DERA G1–G9', desc: 'Salud, educación, seguridad, deporte', badge: '11.282 registros', icon: IconDatabase, tier: 'sectoriales' },
  { name: 'DERA G10', desc: 'Infraestructuras energéticas y medioambientales', badge: '8.300 registros', icon: IconDatabase, tier: 'sectoriales' },
  { name: 'DERA G3', desc: 'Infraestructura hidráulica', badge: '26.266 registros', icon: IconDatabase, tier: 'sectoriales' },
  { name: 'DERA Viaria', desc: 'Red de carreteras de Andalucía', badge: '184K tramos', icon: IconMap, tier: 'sectoriales' },
  { name: 'AGOL R2', desc: 'Datos complementarios IECA', badge: '36K+ registros', icon: IconChart, tier: 'sectoriales' },

  // ─── Patrimoniales ───
  { name: 'IAPH R2', desc: 'Patrimonio histórico de Andalucía', badge: '18.912 · 730 munic.', icon: IconDatabase, tier: 'patrimoniales' },
  { name: 'CGPHA R2', desc: 'Patrimonio catalogado BIC', badge: '18.478 · 730 munic.', icon: IconShieldCheck, tier: 'patrimoniales' },
  { name: 'OBPH R2', desc: 'Patrimonio no catalogado', badge: '8.305 · 672 munic.', icon: IconDatabase, tier: 'patrimoniales' },

  // ─── Especializadas ───
  { name: 'OSM Security', desc: 'Policía local y nacional (OpenStreetMap)', badge: '791 · 420 munic.', icon: IconShieldCheck, tier: 'especializadas' },
  { name: 'Edu Centers', desc: 'Centros educativos Junta de Andalucía', badge: '7.100 · 695 munic.', icon: IconPlus, tier: 'especializadas' },
  { name: 'GC Facilities', desc: 'Cuarteles de la Guardia Civil', badge: '629 · 413 munic.', icon: IconShieldCheck, tier: 'especializadas' },

  // ─── Fallback ───
  { name: 'CartoCiudad', desc: 'Geocodificación IGN nacional', badge: 'API en línea', icon: IconSearch, tier: 'fallback' },
  { name: 'Catastro API', desc: 'Consulta activa por referencia catastral (RC) desde PTEL Tool', badge: 'RC · Tiempo real', icon: IconDatabase, tier: 'fallback',
    modePill: 'API en línea' },
  { name: 'IDEE', desc: 'Infraestructura de Datos Espaciales de España', badge: 'API en línea', icon: IconGlobe, tier: 'fallback' },
  { name: 'INE', desc: 'Códigos municipales oficiales (validación)', badge: 'Referencia', icon: IconChart, tier: 'fallback' },
  { name: 'Nominatim', desc: 'OpenStreetMap (fallback final)', badge: '1 req/s', icon: IconGlobe, tier: 'fallback' },
];
